import { useDropzone } from 'react-dropzone'
import { RefreshCw, Trash2, Upload } from 'lucide-react'
import { Button } from '@teach/ui'
import { videoUploadBridge } from '@/features/editor/lib/videoUploadBridge'
import { useCanvasBridge } from './useCanvasBridge'
import { useVideoUploadPlace } from './useVideoUploadPlace'
import type { VideoSlot } from './videoUploadStore'
import { ImageUploadEmptyState } from './ImageUploadEmptyState'
import { UploadProgressOverlay } from './UploadProgressOverlay'
import { VideoPlayerPreview } from './VideoPlayerPreview'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface VideoUploadProps {
  videoId: string | null
  title: string
  onUploaded: (videoId: string) => void
  onRemove?: () => void
  label?: string
  disabled?: boolean
  /** Слот языка видео-блока: вместе с открытым блоком называет МЕСТО загрузки,
   *  по которому она переживает уход автора и сохраняется в фоне. */
  slot?: VideoSlot
}

export const VideoUpload = ({
  videoId,
  title,
  onUploaded,
  onRemove,
  label,
  disabled = false,
  slot,
}: VideoUploadProps) => {
  const m = useMessages(messages)
  // Загрузка живёт в хранилище над панелью, а не здесь: уход из блока её не
  // обрывает, возврат показывает идущий прогресс (`useVideoUploadPlace`).
  const { progress, start } = useVideoUploadPlace(slot, onUploaded)

  const onDrop = (files: File[], canvas = false) => {
    if (disabled || progress !== null) return
    const file = files[0]
    if (!file) return
    start(file, title, canvas)
  }

  const { getRootProps, getInputProps, open } = useDropzone({
    // Обёртка: второй аргумент dropzone — отказы, а не признак холста.
    onDrop: (files) => onDrop(files),
    accept: { 'video/*': [] },
    multiple: false,
    disabled: disabled || progress !== null,
    noClick: !!videoId,
  })

  // Файл, брошенный на видео-экран холста, грузится здесь же; полоса — и здесь, и на
  // холсте, откуда бы ни начали: автор смотрит на тот экран, с которого работает.
  useCanvasBridge(videoUploadBridge, { onDrop, open, progress, disabled, alwaysOnCanvas: true })

  const uploading = progress !== null
  return videoId ? (
    <div className="relative aspect-video overflow-hidden rounded-2xl border border-border bg-muted/30">
      <input {...getInputProps()} />
      <VideoPlayerPreview videoId={videoId} />
      <UploadProgressOverlay progress={progress} />
      {/* Те же иконки, что у картинки: заменить — тот же путь загрузки, удалить — снять. */}
      <div className="absolute right-2 top-2 flex gap-1.5">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="rounded-lg"
          title={m.replaceVideo}
          aria-label={m.replaceVideo}
          onClick={open}
          disabled={disabled || uploading}
        >
          <RefreshCw className="size-4" />
        </Button>
        {onRemove ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="rounded-lg"
            title={m.removeVideo}
            aria-label={m.removeVideo}
            onClick={onRemove}
            disabled={disabled || uploading}
          >
            <Trash2 className="size-4" />
          </Button>
        ) : null}
      </div>
    </div>
  ) : (
    <ImageUploadEmptyState
      icon={Upload}
      label={label ?? m.videoUploadLabel}
      subtitle={m.videoFormats}
      rootProps={getRootProps({ title: disabled ? m.noRights : undefined })}
      inputProps={getInputProps()}
      disabled={disabled}
      uploading={uploading}
      progress={progress}
    />
  )
}
